// Buscador universal (Ctrl/⌘ K o /): buscar personas y empresas, marcar un numero, crear o ir a.
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { type Acceso, BovedaInactiva, listarAccesos } from '../api/boveda';
import { buscarEnServidor, type Resultado } from '../api/conocimiento';
import { buscarEmpresas, buscarPersonas } from '../api/crm';
import { useSession } from '../auth/session';
import { MENU, puedeVer } from './menu';
import type { Empresa, Persona } from '../api/types';
import { useOportunidades } from '../data/queries';
import { useDebounced, usePhone } from '../hooks';
import { normalize } from '../lib/format';
import { Icon, type IconName } from '../lib/icons';
import { formatPhone, looksLikeNumber } from '../lib/phone-number';
import { SpringValue, reducedMotion } from '../lib/spring';
import { phone } from '../phone/store';
import { Avatar } from '../ui/bits';
import { abrirDialogo } from '../ui/forms';
import { setApariencia, useApariencia } from '../ui/prefs';

// Si la boveda respondio que no esta activa, no se le vuelve a preguntar en esta sesion de la pagina.
let bovedaApagada = false;

type Item = { grupo: string; icon?: IconName; avatar?: string; titulo: string; sub?: string; run: () => void; llamar?: () => void };

export function CommandPalette({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('');
  const [i, setI] = useState(0);
  const dq = useDebounced(q, 160);
  const nav = useNavigate();
  const s = usePhone();
  const ap = useApariencia();
  const { puede, tieneModulo } = useSession();
  const opps = useOportunidades();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [articulos, setArticulos] = useState<Resultado[]>([]);
  const [accesos, setAccesos] = useState<Acceso[]>([]);
  const [buscando, setBuscando] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = box.current;
    if (el && !reducedMotion()) {
      new SpringValue(
        0.96,
        (v) => {
          el.style.transform = `translateX(-50%) scale(${v})`;
          el.style.opacity = String(Math.min(1, (v - 0.96) / 0.03));
        },
        0.0008,
      ).go(1, { response: 0.28 });
    }
  }, []);

  useEffect(() => {
    let vivo = true;
    const t = dq.trim();
    if (t.length < 2) {
      setPersonas([]);
      setEmpresas([]);
      setArticulos([]);
      setAccesos([]);
      return;
    }
    // Boveda: solo titulos (la lista nunca trae claves); si no esta activa, nada.
    if (!looksLikeNumber(t) && !bovedaApagada)
      listarAccesos({ q: t })
        .then((r) => vivo && setAccesos(r.accesos.slice(0, 4)))
        .catch((e) => {
          if (e instanceof BovedaInactiva) bovedaApagada = true;
          if (vivo) setAccesos([]);
        });
    else setAccesos([]);
    // Base de conocimiento (buscador del servidor): llega aparte para no demorar contactos y empresas.
    if (!looksLikeNumber(t))
      buscarEnServidor(t)
        .then((r) => vivo && setArticulos(r?.articulos.slice(0, 5) ?? []))
        .catch(() => vivo && setArticulos([]));
    else setArticulos([]);
    setBuscando(true);
    Promise.all([
      buscarPersonas(t, 6).catch(() => ({ items: [] as Persona[] })),
      looksLikeNumber(t) ? Promise.resolve({ items: [] as Empresa[] }) : buscarEmpresas(t, 4).catch(() => ({ items: [] as Empresa[] })),
    ])
      .then(([p, e]) => {
        if (!vivo) return;
        setPersonas(p.items);
        setEmpresas(e.items);
      })
      .finally(() => vivo && setBuscando(false));
    return () => {
      vivo = false;
    };
  }, [dq]);

  const items: Item[] = useMemo(() => {
    const out: Item[] = [];
    const t = q.trim();
    const nq = normalize(t);
    const ir = (ruta: string) => () => {
      nav(ruta);
      onClose();
    };
    if (looksLikeNumber(t)) out.push({ grupo: 'Acciones', icon: 'phone', titulo: `Llamar a ${formatPhone(t)}`, sub: 'Enter', run: () => (phone.dial(t), onClose()) });
    if (personas[0]?.telefono && !looksLikeNumber(t))
      out.push({
        grupo: 'Acciones',
        icon: 'phone',
        titulo: `Llamar a ${personas[0].nombreCompleto}`,
        sub: formatPhone(personas[0].telefono),
        run: () => (phone.dial(personas[0].telefono, { id: personas[0].id, nombre: personas[0].nombreCompleto, empresa: personas[0].empresa?.nombre }), onClose()),
      });
    const acciones: Item[] = [
      { grupo: 'Acciones', icon: 'plus', titulo: 'Nuevo contacto', run: () => (onClose(), abrirDialogo({ tipo: 'persona' })) },
      { grupo: 'Acciones', icon: 'plus', titulo: 'Nueva empresa', run: () => (onClose(), abrirDialogo({ tipo: 'empresa' })) },
      { grupo: 'Acciones', icon: 'plus', titulo: 'Nueva oportunidad', run: () => (onClose(), abrirDialogo({ tipo: 'oportunidad' })) },
      { grupo: 'Acciones', icon: 'task', titulo: 'Nueva tarea', run: () => (onClose(), abrirDialogo({ tipo: 'tarea' })) },
      { grupo: 'Acciones', icon: 'keypad', titulo: 'Abrir el teclado del teléfono', run: () => (onClose(), phone.expand('teclado')) },
      {
        grupo: 'Acciones',
        icon: 'sliders',
        titulo: ap.tema === 'dark' ? 'Usar tema claro' : 'Usar tema oscuro',
        run: () => (setApariencia({ tema: ap.tema === 'dark' ? 'light' : 'dark' }), onClose()),
      },
    ];
    if (s.config.modo === 'demo') acciones.unshift({ grupo: 'Acciones', icon: 'phoneIn', titulo: 'Simular llamada entrante', run: () => (onClose(), phone.simularEntrante()) });
    out.push(...acciones.filter((a) => !nq || normalize(a.titulo).includes(nq)));
    [...MENU.filter((i) => puedeVer(i, { puede, tieneModulo })), { ruta: '/perfil', nombre: 'Mi perfil', icono: 'user' as const, nombreModulo: 'Tu cuenta' }]
      .filter((i) => !nq || normalize(`${i.nombre} ${i.nombreModulo}`).includes(nq))
      .forEach((i) => out.push({ grupo: 'Ir a', icon: i.icono, titulo: i.nombre, sub: i.nombreModulo, run: ir(i.ruta) }));
    personas.forEach((p) =>
      out.push({
        grupo: 'Contactos',
        avatar: p.nombreCompleto,
        titulo: p.nombreCompleto,
        sub: [p.empresa?.nombre, p.telefono ? formatPhone(p.telefono) : ''].filter(Boolean).join(' · '),
        run: ir(`/personas/${p.id}`),
        llamar: p.telefono ? () => (phone.dial(p.telefono, { id: p.id, nombre: p.nombreCompleto, empresa: p.empresa?.nombre }), onClose()) : undefined,
      }),
    );
    empresas.forEach((e) => out.push({ grupo: 'Empresas', icon: 'building', titulo: e.nombre, sub: e.ciudad, run: ir(`/empresas/${e.id}`) }));
    articulos.forEach((a) => out.push({ grupo: 'Conocimiento', icon: 'book', titulo: a.titulo, sub: a.categoria, run: ir(`/bandeja/conocimiento?articulo=${a.id}`) }));
    accesos.forEach((a) => out.push({ grupo: 'Accesos', icon: 'lock', titulo: a.titulo, sub: a.categoria || 'Bóveda', run: ir(`/bandeja/accesos?acceso=${a.id}`) }));
    if (nq.length >= 2)
      (opps.data ?? [])
        .filter((o) => normalize(`${o.nombre} ${o.empresa?.nombre ?? ''}`).includes(nq))
        .slice(0, 4)
        .forEach((o) =>
          out.push({ grupo: 'Oportunidades', icon: 'kanban', titulo: o.nombre, sub: o.empresa?.nombre, run: () => (onClose(), abrirDialogo({ tipo: 'verOportunidad', id: o.id })) }),
        );
    return out;
  }, [q, personas, empresas, articulos, accesos, opps.data, nav, onClose, s.config.modo, ap.tema, puede, tieneModulo]);

  useEffect(() => setI(0), [items.length, q]);
  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [i]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setI((x) => Math.min(items.length - 1, x + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setI((x) => Math.max(0, x - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const it = items[i];
      if (!it) return;
      if (e.shiftKey && it.llamar) it.llamar();
      else it.run();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  let grupo = '';
  return createPortal(
    <>
      <div className="scrim" onClick={onClose} />
      <div className="cmdk" ref={box} role="dialog" aria-label="Buscador universal">
        <div className="field">
          <Icon name="search" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Busca, marca un número o escribe una acción"
            autoComplete="off"
            aria-label="Buscar"
            data-testid="buscador"
          />
          <kbd>Esc</kbd>
        </div>
        <div className="cmdk-list" role="listbox" ref={list}>
          {items.length === 0 ? <div className="cmdk-empty">{buscando ? 'Buscando…' : 'Sin resultados. Escribe un número para llamarlo.'}</div> : null}
          {items.map((it, k) => {
            const cab =
              it.grupo !== grupo
                ? ((grupo = it.grupo),
                  (
                    <div key={`g-${it.grupo}`} className="cmdk-g lbl">
                      {it.grupo}
                    </div>
                  ))
                : null;
            return (
              <div key={`${it.grupo}-${it.titulo}-${k}`}>
                {cab}
                <button className="cmdk-it" role="option" aria-selected={k === i} onMouseMove={() => setI(k)} onClick={it.run}>
                  {it.avatar ? (
                    <Avatar nombre={it.avatar} size="sm" />
                  ) : (
                    <span className="ico-dot">
                      <Icon name={it.icon ?? 'dots'} />
                    </span>
                  )}
                  <span className="t">{it.titulo}</span>
                  {it.sub ? <small>{it.sub}</small> : null}
                </button>
              </div>
            );
          })}
        </div>
        <div className="cmdk-f">
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> moverse
          </span>
          <span>
            <kbd>Enter</kbd> abrir
          </span>
          <span>
            <kbd>⇧ Enter</kbd> llamar
          </span>
        </div>
      </div>
    </>,
    document.body,
  );
}
